import React, { useState, useEffect, useCallback } from 'react';
import { FolderPlus, File, RefreshCw } from 'lucide-react';
import FolderItem from './FolderItem';
import { folderService } from '../../services/api';

const FolderTree = ({
  caseId,
  selectedFolder,
  onSelectFolder,
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
  onDropDocument,
  onDocumentClick,
  onRefresh
}) => {
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unfiledDragOver, setUnfiledDragOver] = useState(false);

  const loadFolders = useCallback(async () => {
    if (!caseId) {
      setFolders([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await folderService.getFolderTree(caseId);
      setFolders(response.data.folders || []);

      // Auto-expand folders that have subfolders
      const newExpanded = new Set();
      const autoExpand = (folderList) => {
        folderList.forEach(folder => {
          if (folder.subfolders && folder.subfolders.length > 0) {
            newExpanded.add(folder.id);
            autoExpand(folder.subfolders);
          }
        });
      };
      autoExpand(response.data.folders || []);
      setExpandedFolders(newExpanded);
    } catch (err) {
      console.error('Failed to load folders:', err);
      setError('Failed to load folders');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const handleRefresh = useCallback(async () => {
    await loadFolders();
    if (onRefresh) onRefresh();
  }, [loadFolders, onRefresh]);

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleCreateSubfolder = (parentFolder) => {
    onCreateFolder(parentFolder);
  };

  const renderFolderTree = (folderList, level = 0) => {
    return folderList.map(folder => (
      <FolderItem
        key={folder.id}
        folder={folder}
        isSelected={selectedFolder?.id === folder.id}
        isExpanded={expandedFolders.has(folder.id)}
        onSelect={onSelectFolder}
        onToggle={toggleFolder}
        onEdit={onEditFolder}
        onDelete={onDeleteFolder}
        onCreateSubfolder={handleCreateSubfolder}
        onDropDocument={onDropDocument}
        onDocumentClick={onDocumentClick}
        level={level}
      />
    ));
  };

  return (
    <div className="h-full flex flex-col bg-white border-r border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Folders</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title="Refresh folders"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => onCreateFolder(null)}
            className="p-1.5 text-yellow-600 hover:text-yellow-600 hover:bg-gray-50 rounded"
            title="New folder"
          >
            <FolderPlus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Folder Tree */}
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
          </div>
        ) : error ? (
          <div className="p-4 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-2 text-sm text-yellow-600 hover:text-yellow-600"
            >
              Try again
            </button>
          </div>
        ) : folders.length === 0 ? (
          <div className="p-4 text-center">
            <p className="text-sm text-gray-500 mb-2">No folders yet</p>
            <button
              onClick={() => onCreateFolder(null)}
              className="text-sm text-yellow-600 hover:text-yellow-600"
            >
              Create your first folder
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            {renderFolderTree(folders)}
          </div>
        )}
      </div>

      {/* Unfiled Documents */}
      <div className="border-t border-gray-200">
        <div
          className={`
            flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors
            ${selectedFolder?.id === 'unfiled' ? 'bg-gray-50 text-yellow-600' : 'hover:bg-gray-100 text-gray-700'}
            ${unfiledDragOver ? 'bg-gray-100 border-2 border-blue-400 border-dashed' : ''}
          `}
          onClick={() => onSelectFolder({ id: 'unfiled', name: 'Unfiled Documents' })}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setUnfiledDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setUnfiledDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setUnfiledDragOver(false);
            const documentId = e.dataTransfer.getData('application/document-id');
            if (documentId && onDropDocument) {
              onDropDocument(documentId, null); // null means unfiled
            }
          }}
        >
          <File className="h-4 w-4 text-gray-400" />
          <span className="flex-1 text-sm font-medium">Unfiled Documents</span>
        </div>
      </div>
    </div>
  );
};

export default FolderTree;
